import React, { useEffect, useState, useContext } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";
import { AuthContext } from "../helpers/AuthContext";

function ToolsGroups() {
  const { authState } = useContext(AuthContext);
  const [ToolsGroupsObject, setToolsGroupsObject] = useState([]);
  const [categoriesByGroup, setCategoriesByGroup] = useState({});
  const [toolCounts, setToolCounts] = useState({});
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editStoragePositions, setEditStoragePositions] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function fetchConfig() {
      let url = await loadConfig();
      setApiUrl(url.APIURL);
      setLoadingConfig(false);
    }
    fetchConfig();
  }, []);

  const fetchGroups = React.useCallback(async () => {
    const response = await axios.get(`${apiUrl}/toolsgroups`, {
      headers: { accessToken: localStorage.getItem("accessToken") }
    });
    setToolsGroupsObject(response.data);
    // Fetch categories and tool counts for each group
    const categoriesMap = {};
    const toolCountsMap = {};
    await Promise.all(
      response.data.map(async (group) => {
        try {
          const catRes = await axios.get(`${apiUrl}/categories/toolsgroup/${group.id}`, {
            headers: { accessToken: localStorage.getItem("accessToken") }
          });
          categoriesMap[group.id] = catRes.data;
        } catch {
          categoriesMap[group.id] = [];
        }
        // Fetch tools to count them
        try {
          const toolsRes = await axios.get(`${apiUrl}/tools`, {
            headers: { accessToken: localStorage.getItem("accessToken") }
          });
          const toolsInGroup = toolsRes.data.filter(t => t.ToolsGroupId === group.id);
          toolCountsMap[group.id] = toolsInGroup.length;
        } catch {
          toolCountsMap[group.id] = 0;
        }
      })
    );
    setCategoriesByGroup(categoriesMap);
    setToolCounts(toolCountsMap);
  }, [apiUrl]);

  useEffect(() => {
    if (!loadingConfig) {
      fetchGroups();
    }
  }, [loadingConfig, apiUrl, fetchGroups]);

  const handleEditClick = (group) => {
    setEditId(group.id);
    let positions = group.storagePositions;
    if (typeof positions === "string") {
      try {
        positions = JSON.parse(positions);
      } catch {
        positions = [];
      }
    }
    setEditStoragePositions(Array.isArray(positions) && positions.length > 0 ? positions.join(", ") : "");
  };

  const handleSaveStoragePositions = async (groupId) => {
    setSaving(true);
    const storagePositionsArr = editStoragePositions
      ? editStoragePositions.split(",").map(opt => opt.trim()).filter(opt => opt)
      : [];
    try {
      await axios.put(`${apiUrl}/toolsgroups/${groupId}`, { storagePositions: storagePositionsArr }, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      await fetchGroups();
      setEditId(null);
      setEditStoragePositions("");
    } catch (err) {
      alert("Fehler beim Aktualisieren der Lagerort Optionen.");
    }
    setSaving(false);
  };

  const handleToggleClothing = async (group) => {
    try {
      await axios.put(`${apiUrl}/toolsgroups/${group.id}`, { isClothing: !group.isClothing }, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      await fetchGroups();
    } catch (err) {
      const errorMsg = err.response?.data?.error || "Fehler beim Aktualisieren der Kleidungsgruppen-Einstellung.";
      alert(errorMsg);
    }
  };

  const handleDeleteGroup = async (groupId, groupName) => {
    if (!window.confirm(`Möchten Sie die Gerätegruppe "${groupName}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    try {
      await axios.delete(`${apiUrl}/toolsgroups/${groupId}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      alert("Gerätegruppe erfolgreich gelöscht");
      await fetchGroups();
    } catch (e) {
      const errorMsg = e.response?.data?.error || "Fehler beim Löschen der Gerätegruppe.";
      alert(errorMsg);
    }
  };

  if (loadingConfig) {
    return <LoadingSpinner text="Lade Gerätegruppen..." />;
  }

  if (ToolsGroupsObject.length === 0) {
    return (
      <div className="container">
        <h1 className="my-4">Gerätegruppen</h1>
        {authState.isAdmin && (
          <div className="mb-4">
            <Link to="/ToolsGroups/add" className="btn btn-primary">Gerätegruppe hinzufügen</Link>
          </div>
        )}
        <div className="alert alert-info">Keine Gerätegruppen vorhanden.</div>
      </div>
    );
  }

  return (
    <>
    <div className="container">
      <h1 className="my-4">Gerätegruppen</h1>
      {authState.isAdmin && (
        <div className="mb-4">
          <Link to="/ToolsGroups/add" className="btn btn-primary">Gerätegruppe hinzufügen</Link>
        </div>
      )}
      <div className="mb-3" style={{ marginTop: '20px' }}>
        <input
          type="text"
          className="form-control"
          placeholder="Suche nach Gruppenname..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>Gruppenname</th>
            <th>Lagerort Optionen</th>
            <th>Prüfkategorien</th>
            <th>Kleidung</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {ToolsGroupsObject.filter(group => {
            const groupname = group.groupname?.toLowerCase() || "";
            const searchTerm = search.toLowerCase();
            return groupname.includes(searchTerm);
          }).map((value, key) => {
            const isEditing = editId === value.id;
            // Remove inline editor for check categories
            return (
              <tr key={key}>
                <td>{value.groupname}</td>
                <td>
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={editStoragePositions}
                        onChange={e => setEditStoragePositions(e.target.value)}
                        placeholder="z.B. Regal 1, Regal 2, Fach B"
                        className="form-control"
                        style={{ minWidth: "200px" }}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ marginLeft: "8px" }}
                        onClick={e => { e.stopPropagation(); handleSaveStoragePositions(value.id); }}
                        disabled={saving}
                      >
                        Speichern
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        style={{ marginLeft: "4px" }}
                        onClick={e => { e.stopPropagation(); setEditId(null); }}
                        disabled={saving}
                      >
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    <>
                      {Array.isArray(value.storagePositions) ? value.storagePositions.join(", ") : ""}
                      {authState.isAdmin && (
                        <button
                          className="btn btn-link btn-sm"
                          style={{ marginLeft: "8px" }}
                          onClick={e => { e.stopPropagation(); handleEditClick(value); }}
                        >
                          Lagerort Optionen bearbeiten
                        </button>
                      )}
                    </>
                  )}
                </td>
                <td>
                  {Array.isArray(categoriesByGroup[value.id]) && categoriesByGroup[value.id].length > 0 ? (
                    <ul style={{ paddingLeft: "18px" }}>
                      {categoriesByGroup[value.id].map((cat, idx) => (
                        <li key={idx}>
                          {cat.name}
                          {cat.interval ? ` (${cat.interval} ${cat.unit || ''})` : ''}
                        </li>
                      ))}
                    </ul>
                  ) : <span className="text-muted">Keine Prüfkategorien</span>}
                </td>
                <td>
                  {value.isClothing ? (
                    <span style={{ color: 'green' }}>Ja</span>
                  ) : (
                    <span className="text-muted">Nein</span>
                  )}
                  {authState.isAdmin && (
                    value.hasWashLogs ? (
                      <span
                        className="text-muted"
                        style={{ marginLeft: "8px", fontStyle: "italic" }}
                        title="Kann nicht mehr geändert werden, da bereits Wascheinträge vorhanden sind"
                      >
                        gesperrt
                      </span>
                    ) : (
                      <button
                        className="btn btn-link btn-sm"
                        style={{ marginLeft: "8px" }}
                        onClick={e => { e.stopPropagation(); handleToggleClothing(value); }}
                      >
                        ändern
                      </button>
                    )
                  )}
                </td>
                <td>
                  {authState.isAdmin && (
                    <Link
                      to={`/edit-group-categories/${value.id}`}
                      className="btn btn-primary btn-sm"
                      style={{ marginLeft: "8px" }}
                      onClick={e => e.stopPropagation()}
                    >
                      Prüfkategorien bearbeiten
                    </Link>
                  )}
                  {authState.isAdmin && toolCounts[value.id] === 0 && (
                    <button
                      className="btn btn-danger btn-sm"
                      style={{ marginLeft: "8px" }}
                      onClick={e => { e.stopPropagation(); handleDeleteGroup(value.id, value.groupname); }}
                    >
                      Löschen
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

export default ToolsGroups;
